import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { API } from "../services/api";

export const ACCOUNT_LIMIT_MIN = 1;
export const ACCOUNT_LIMIT_MAX = 500;
export const INVALID_ACCOUNT_LIMIT = `La limite doit être comprise entre ${ACCOUNT_LIMIT_MIN} et ${ACCOUNT_LIMIT_MAX} comptes.`;

/** Libellés des modules du portail EHPAD : aucune clé technique n'est affichée telle quelle. */
export const EHPAD_MODULES = {
    STOCK_DBU: {
        label: "Stock de la collectivité",
        description: "Inventaire, entrées et sorties du stock de médicaments de l'établissement.",
    },
    STOCK_HORS_PILULIER: {
        label: "Hors pilulier",
        description: "Suivi des traitements délivrés hors pilulier pour chaque résident.",
    },
};

/** « 5 comptes actifs sur 12 » ou « 5 comptes actifs · sans limite ». */
export function accountUsage(used, limit) {
    const plural = used > 1 ? "s" : "";
    const accounts = `${used} compte${plural} actif${plural}`;
    return limit == null ? `${accounts} · sans limite` : `${accounts} sur ${limit}`;
}

/** Enregistre l'abonnement préparé par {@link EhpadSubscriptionFields}. */
export async function saveEhpadSubscription(ehpadId, payload) {
    const { data } = await API.put(`/admin/ehpads/${ehpadId}/subscription`, payload);
    return data;
}

function clampLimit(value) {
    return Math.min(ACCOUNT_LIMIT_MAX, Math.max(ACCOUNT_LIMIT_MIN, value));
}

function parseLimit(value) {
    const limit = Number.parseInt(value, 10);
    return Number.isInteger(limit) && limit >= ACCOUNT_LIMIT_MIN && limit <= ACCOUNT_LIMIT_MAX ? limit : null;
}

function toDraft(subscription) {
    return {
        unlimited: subscription.accountLimit == null,
        accountLimit: String(subscription.accountLimit ?? Math.max(subscription.activeAccounts, ACCOUNT_LIMIT_MIN)),
        modules: Object.fromEntries(subscription.modules.map(module => [module.key, module.active])),
    };
}

/** Corps de la requête d'enregistrement, ou une erreur si la limite saisie est invalide. */
function toChange(draft) {
    const limit = parseLimit(draft.accountLimit);
    if (!draft.unlimited && limit === null) return { payload: null, error: INVALID_ACCOUNT_LIMIT };
    return {
        payload: {
            accountLimit: draft.unlimited ? null : limit,
            modules: Object.entries(draft.modules).map(([key, active]) => ({ key, active })),
        },
        error: null,
    };
}

/**
 * Champs d'abonnement de l'EHPAD au portail (nombre de comptes autorisés et modules ouverts),
 * intégrés au formulaire « Éditer » : le parent enregistre via {@code onChange} et son propre bouton.
 * {@code onChange} reçoit {@code { payload, error }} ; payload reste null tant que rien n'est chargé.
 */
export default function EhpadSubscriptionFields({ ehpad, onChange }) {
    const [activeAccounts, setActiveAccounts] = useState(0);
    const [draft, setDraft] = useState(null);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        API.get(`/admin/ehpads/${ehpad.id}/subscription`)
            .then(({ data }) => {
                if (cancelled) return;
                const loaded = toDraft(data);
                setActiveAccounts(data.activeAccounts);
                setDraft(loaded);
                onChange(toChange(loaded));
            })
            .catch(err => {
                if (!cancelled) setLoadError(err.response?.data?.message ?? "Impossible de charger l'abonnement.");
            });
        return () => { cancelled = true; };
        // onChange n'est appelé qu'au chargement : le recharger à chaque rendu du parent n'aurait pas de sens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ehpad.id]);

    // Le parent est prévenu dans le même événement que la saisie, pour que son bouton Enregistrer voie la dernière valeur.
    const update = (key, value) => {
        const next = { ...draft, [key]: value };
        setDraft(next);
        onChange(toChange(next));
    };

    if (!draft) {
        return (
            <div style={panelStyle}>
                {loadError ? <p style={errorStyle}>{loadError}</p> : <p style={mutedStyle}>Chargement de l'abonnement...</p>}
            </div>
        );
    }

    const limit = parseLimit(draft.accountLimit);
    const effective = draft.unlimited ? null : limit;
    const step = (delta) => update("accountLimit", String(clampLimit((limit ?? ACCOUNT_LIMIT_MIN) + delta)));

    return (
        <div style={panelStyle} role="group" aria-label={`Abonnement de ${ehpad.nom}`}>
            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
                <section style={sectionStyle} aria-labelledby={`ehpad-${ehpad.id}-pack-title`}>
                    <h3 id={`ehpad-${ehpad.id}-pack-title`} style={sectionTitleStyle}>Abonnement</h3>
                    <p style={mutedStyle}>
                        Nombre de comptes actifs autorisés sur le portail EHPAD : membres et responsables, invitations en attente comprises.
                    </p>
                    <label style={{ ...switchRowStyle, display: "inline-flex" }}>
                        <input
                            type="checkbox"
                            role="switch"
                            style={switchStyle}
                            checked={draft.unlimited}
                            onChange={event => update("unlimited", event.target.checked)}
                        />
                        <span>Comptes illimités</span>
                    </label>
                    {!draft.unlimited && (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "10px" }}>
                            <button
                                type="button"
                                aria-label="Retirer un compte"
                                disabled={limit !== null && limit <= ACCOUNT_LIMIT_MIN}
                                onClick={() => step(-1)}
                                style={stepButtonStyle}
                            >
                                −
                            </button>
                            <input
                                type="number"
                                aria-label="Limite de comptes actifs"
                                min={ACCOUNT_LIMIT_MIN}
                                max={ACCOUNT_LIMIT_MAX}
                                step={1}
                                value={draft.accountLimit}
                                onChange={event => update("accountLimit", event.target.value)}
                                style={{ ...inputStyle, width: "90px", textAlign: "center" }}
                            />
                            <button
                                type="button"
                                aria-label="Ajouter un compte"
                                disabled={limit !== null && limit >= ACCOUNT_LIMIT_MAX}
                                onClick={() => step(1)}
                                style={stepButtonStyle}
                            >
                                +
                            </button>
                        </div>
                    )}
                    <p style={{ ...mutedStyle, marginTop: "10px" }}>{accountUsage(activeAccounts, effective)}</p>
                    {effective !== null && effective < activeAccounts && (
                        <p style={warnStyle}>
                            Cette limite est inférieure aux {activeAccounts} comptes actifs : ils restent actifs, mais aucun
                            nouveau compte ne pourra être activé tant que la limite n'est pas relevée.
                        </p>
                    )}
                </section>

                <section style={sectionStyle} aria-labelledby={`ehpad-${ehpad.id}-modules-title`}>
                    <h3 id={`ehpad-${ehpad.id}-modules-title`} style={sectionTitleStyle}>Modules</h3>
                    <p style={mutedStyle}>Fonctionnalités du portail ouvertes à l'établissement.</p>
                    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "10px" }}>
                        {Object.keys(draft.modules).map(key => {
                            const info = EHPAD_MODULES[key] ?? {
                                label: "Module complémentaire",
                                description: "Fonctionnalité réservée à certains établissements.",
                            };
                            const descriptionId = `ehpad-${ehpad.id}-module-${key}-description`;
                            return (
                                <li key={key}>
                                    <label style={{ ...switchRowStyle, alignItems: "flex-start" }}>
                                        <input
                                            type="checkbox"
                                            role="switch"
                                            style={{ ...switchStyle, marginTop: "3px" }}
                                            checked={draft.modules[key]}
                                            aria-label={info.label}
                                            aria-describedby={descriptionId}
                                            onChange={event => update("modules", { ...draft.modules, [key]: event.target.checked })}
                                        />
                                        <span>
                                            <span style={moduleNameStyle}>{info.label}</span>
                                            <span id={descriptionId} style={hintStyle}>{info.description}</span>
                                        </span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            </div>
        </div>
    );
}

EhpadSubscriptionFields.propTypes = {
    ehpad: PropTypes.shape({
        id: PropTypes.number.isRequired,
        nom: PropTypes.string,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
};

// Styles alignés sur les champs « Éditer » de la liste des EHPADs (AdminDashboard : labelStyle, inputStyle, aides).
const panelStyle = { borderTop: "1px solid #e5e7eb", paddingTop: "14px" };
const sectionStyle = { flex: "1 1 280px", minWidth: 0 };
const sectionTitleStyle = { display: "block", margin: "0 0 6px", fontSize: "12px", fontWeight: 600, color: "#6b7280" };
const mutedStyle = { margin: "0 0 10px", fontSize: "12px", color: "#6b7280" };
const hintStyle = { display: "block", fontSize: "12px", color: "#6b7280", marginTop: "2px" };
const moduleNameStyle = { display: "block", fontSize: "14px", fontWeight: 400, color: "#111827" };
const switchRowStyle = {
    display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", color: "#111827",
    padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: "8px", background: "white", boxSizing: "border-box",
};
const switchStyle = { width: "16px", height: "16px", margin: 0, flexShrink: 0, cursor: "pointer", accentColor: "#6c4de6" };
const inputStyle = { padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box", color: "#111827", background: "white" };
const stepButtonStyle = { width: "34px", height: "34px", border: "1px solid #d1d5db", borderRadius: "7px", background: "white", cursor: "pointer", fontSize: "16px", color: "#374151" };
const warnStyle = { margin: "6px 0 0", fontSize: "13px", color: "#b45309" };
const errorStyle = { margin: 0, fontSize: "13px", color: "#dc2626" };
